/** Per-session drop-off bar chart used by the drill-in view.
 *
 * Each session is rendered as a vertical bar sized by `trials_attempted`.
 * Sessions with a non-null `bail_trial_index` are highlighted and overlaid
 * with a marker at the bail position so clinicians can see where bails
 * cluster across sessions.
 *
 * Pure inline SVG (no chart library) — consistent with the rollup
 * Sparkline primitive already used elsewhere in this directory.
 */
import type { ReactElement } from 'react';

export interface DropoffSession {
  id: string;
  started_at: string;
  trials_attempted: number | null;
  bail_trial_index: number | null;
}

interface Props {
  sessions: DropoffSession[];
  /** Optional fallback when the chart cannot render (e.g. no data). */
  emptyLabel?: string;
}

interface Bar {
  index: number;
  trials: number;
  bail: number | null;
}

const WIDTH = 320;
const HEIGHT = 120;
const PADDING_X = 24;
const PADDING_Y = 16;
const BAR_GAP = 6;

function buildBars(sessions: DropoffSession[]): Bar[] {
  return sessions.map((session, index) => ({
    index,
    trials: Math.max(0, session.trials_attempted ?? 0),
    bail: session.bail_trial_index != null ? session.bail_trial_index + 1 : null,
  }));
}

function summarize(bars: Bar[]): string {
  const bails = bars.filter((bar) => bar.bail != null);
  if (bails.length === 0) return 'No drop-off recorded across sessions.';
  const bailTrials = bails.map((bar) => bar.bail ?? 0);
  const summary = bails
    .map((bar) => `Session ${bar.index + 1} bailed at trial ${bar.bail}`)
    .join('; ');
  return `Drop-off observed in ${bails.length} of ${bars.length} session(s); bail trial(s): ${bailTrials.join(', ')}. ${summary}`;
}

export function DropoffChart({ sessions, emptyLabel }: Props): ReactElement {
  const bars = buildBars(sessions);
  if (bars.length === 0) {
    return (
      <div className="dropoff-chart" aria-label={emptyLabel ?? 'Drop-off chart unavailable'}>
        <p className="tagline">{emptyLabel ?? 'No session data available.'}</p>
      </div>
    );
  }

  const maxTrials = Math.max(1, ...bars.map((bar) => bar.trials));
  const innerHeight = HEIGHT - PADDING_Y * 2;
  // Pick a bar width that fits comfortably inside the 320px base viewBox for
  // small session counts, and let the viewBox grow (with horizontal scrolling
  // on the wrapper) for larger session counts so no bars are clipped.
  const preferredBarWidth = (WIDTH - PADDING_X * 2 - BAR_GAP * Math.max(0, bars.length - 1))
    / Math.max(1, bars.length);
  const MIN_BAR_WIDTH = 4;
  const barWidth = preferredBarWidth < MIN_BAR_WIDTH ? MIN_BAR_WIDTH : preferredBarWidth;
  const contentWidth = bars.length * barWidth + BAR_GAP * Math.max(0, bars.length - 1);
  const viewBoxWidth = Math.max(WIDTH, PADDING_X * 2 + contentWidth);
  const baselineY = PADDING_Y + innerHeight;

  const bails = bars.filter((bar) => bar.bail != null);
  const ariaLabel = summarize(bars);

  return (
    <div className="dropoff-chart" aria-label={ariaLabel} role="img">
      <div className="dropoff-chart-scroll">
        <svg
          width={viewBoxWidth}
          height={HEIGHT}
          viewBox={`0 0 ${viewBoxWidth} ${HEIGHT}`}
          preserveAspectRatio="xMinYMid meet"
          role="presentation"
        >
        {/* Baseline axis */}
        <line
          x1={PADDING_X}
          x2={viewBoxWidth - PADDING_X}
          y1={baselineY}
          y2={baselineY}
          stroke="currentColor"
          strokeOpacity="0.25"
          strokeWidth="1"
        />
        {/* Y-axis tick at the top for orientation */}
        <text
          x={PADDING_X}
          y={PADDING_Y + 4}
          fontSize="10"
          fill="currentColor"
          fillOpacity="0.6"
        >
          {maxTrials}
        </text>
        <text
          x={PADDING_X}
          y={baselineY - 2}
          fontSize="10"
          fill="currentColor"
          fillOpacity="0.6"
        >
          0
        </text>
        {bars.map((bar, i) => {
          const x = PADDING_X + i * (barWidth + BAR_GAP);
          const barHeight = (bar.trials / maxTrials) * innerHeight;
          const y = baselineY - barHeight;
          const isBail = bar.bail != null;
          const bailY =
            bar.bail != null && bar.trials > 0
              ? baselineY - (Math.min(bar.bail, bar.trials) / maxTrials) * innerHeight
              : null;
          return (
            <g key={i}>
              <rect
                x={x}
                y={y}
                width={barWidth}
                height={Math.max(1, barHeight)}
                fill={isBail ? '#c0392b' : 'currentColor'}
                fillOpacity={isBail ? 0.85 : 0.35}
                stroke={isBail ? '#c0392b' : 'currentColor'}
                strokeOpacity={isBail ? 1 : 0.6}
                strokeWidth="1"
              >
                <title>
                  Session {i + 1}: {bar.trials} trial(s) attempted
                  {isBail ? `, bailed at trial ${bar.bail}` : ''}
                </title>
              </rect>
              {bailY != null && (
                <line
                  x1={x}
                  x2={x + barWidth}
                  y1={bailY}
                  y2={bailY}
                  stroke="#c0392b"
                  strokeWidth="2"
                  strokeDasharray="3 2"
                />
              )}
              {isBail && bailY != null && (
                <circle cx={x + barWidth / 2} cy={bailY} r={3} fill="#c0392b">
                  <title>Drop-off at trial {bar.bail}</title>
                </circle>
              )}
            </g>
          );
        })}
      </svg>
      </div>
      <p className="tagline dropoff-chart-legend">
        {bails.length === 0
          ? 'No drop-off recorded across sessions.'
          : `Drop-off in ${bails.length} of ${bars.length} session(s). Red bars mark sessions where the learner bailed; the dashed line marks the trial position.`}
      </p>
    </div>
  );
}

export default DropoffChart;
